/**
 * ErrorBoundary
 *
 * A thin React class component that catches render-time errors from its
 * subtree. The admin app historically had no boundary, which meant a
 * single uncaught exception inside a Mantine modal or route would unmount
 * the whole React tree and leave the user staring at a blank screen
 * until they navigated back and hard-reloaded.
 *
 * The boundary displays a compact Mantine alert with the error message
 * (and, in development, a stack trace) plus a reset button. It also
 * forwards the error to the console so existing logging / Sentry hooks
 * still see it.
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import { Alert, Button, Code, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'

interface Props {
  children: ReactNode
  /** Optional custom label for the top-level error title. */
  title?: string
  /** Optional fallback renderer. Overrides the default UI when provided. */
  fallback?: (error: Error, reset: () => void) => ReactNode
  /** Optional hook invoked on every caught error. */
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info })
    console.error('[ErrorBoundary] caught error:', error, info)
    this.props.onError?.(error, info)
  }

  reset = () => {
    this.setState({ error: null, info: null })
  }

  render() {
    const { error, info } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset)
    }

    const isDev = import.meta.env.DEV

    return (
      <Stack c="black" gap="sm" p="md">
        <Alert
          icon={<IconAlertTriangle />}
          color="red"
          variant="filled"
          title={this.props.title ?? 'Something went wrong'}
        >
          <Text size="sm">{error.message || String(error)}</Text>
        </Alert>
        {isDev && info?.componentStack && (
          <Code block fz="10px">
            {info.componentStack}
          </Code>
        )}
        <Group>
          <Button
            leftSection={<IconRefresh size={14} />}
            variant="default"
            onClick={this.reset}
          >
            Retry
          </Button>
          <Button
            variant="subtle"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </Group>
      </Stack>
    )
  }
}
